import React, { useMemo } from "react";
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { PollResultsResponse } from "../../domain/polls/types";
import { NoticeboardButton } from "../shared";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import {
  getNoticeFeedTypeChipBackground,
  getNoticeFeedTypeChipLabel,
  type NoticeFeedCardType,
} from "../../utils/noticeFeedChips";
import { normalizeViewCount } from "../../utils/viewCount";
import {
  collectPollVoterUserIds,
  enrichPollVotersWithAudience,
} from "../../utils/pollVoterDisplay";

type PollOption = { optionId: string; label: string; order: number };

type AudienceMember = { id: string; name: string; email: string };

type ViewerHint = { id?: string; name: string; email: string };

export type PollDetailsDashboardProps = {
  title: string;
  circleLabel: string;
  createdAt: string;
  viewData: { total: number; seen: number } | null;
  typeChip: NoticeFeedCardType;
  results: PollResultsResponse | null;
  loading: boolean;
  error?: string | null;
  fallbackOptions?: PollOption[];
  audienceMembers: AudienceMember[];
  audienceLoading?: boolean;
  viewerHints?: ViewerHint[];
  isPreview?: boolean;
  canDelete?: boolean;
  sendingReminder?: boolean;
  onSendReminder?: (member?: AudienceMember) => void;
  onDelete?: () => void;
};

function formatPollDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatVoteTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const cardSx = {
  p: { xs: 2, md: 2.5 },
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
};

const PollDetailsDashboard: React.FC<PollDetailsDashboardProps> = ({
  title,
  circleLabel,
  createdAt,
  viewData,
  typeChip,
  results,
  loading,
  error,
  fallbackOptions = [],
  audienceMembers,
  audienceLoading = false,
  viewerHints = [],
  isPreview = false,
  canDelete = false,
  sendingReminder = false,
  onSendReminder,
  onDelete,
}) => {
  const theme = useTheme();
  const normalizedViews = normalizeViewCount(viewData);

  const sortedFallback = [...fallbackOptions].sort((a, b) => a.order - b.order);
  // Claim polls: API `options` is the app voting list (unclaimed only). Staff UIs
  // must use `allOptions` so claimed creatures still resolve to labels (otherwise
  // "Who voted" shows "Voted for Unknown option").
  const isClaimPoll = results?.pollMode === "claim";
  const optionsSource =
    isClaimPoll && results?.allOptions?.length
      ? results.allOptions
      : results?.options;
  const optionsToShow =
    optionsSource?.length
      ? [...optionsSource].sort((a, b) => a.order - b.order)
      : sortedFallback.map((opt) => ({
          ...opt,
          count: 0,
          percentage: 0,
        }));

  const optionLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const opt of optionsToShow) {
      map.set(opt.optionId, opt.label);
    }
    for (const opt of results?.claimedOptions ?? []) {
      map.set(opt.optionId, opt.label);
    }
    for (const opt of sortedFallback) {
      if (!map.has(opt.optionId)) map.set(opt.optionId, opt.label);
    }
    return map;
  }, [optionsToShow, results?.claimedOptions, sortedFallback]);

  const totalVotes = results?.totalVotes ?? 0;
  const totalMembers = Math.max(
    audienceMembers.length,
    viewData?.total ?? 0,
    totalVotes
  );
  const notRespondedCount = Math.max(0, totalMembers - totalVotes);

  const voterUserIds = useMemo(
    () => collectPollVoterUserIds(results?.responses ?? []),
    [results?.responses]
  );

  const voters = useMemo(
    () =>
      enrichPollVotersWithAudience(
        results?.responses ?? [],
        audienceMembers,
        viewerHints
      ),
    [results?.responses, audienceMembers, viewerHints]
  );

  const notRespondedMembers = useMemo(() => {
    if (audienceMembers.length === 0) {
      return [];
    }
    return audienceMembers.filter((member) => !voterUserIds.has(member.id));
  }, [audienceMembers, voterUserIds]);

  const viewedOnlyCount = useMemo(() => {
    return viewerHints.filter(
      (viewer) => viewer.id && !voterUserIds.has(viewer.id)
    ).length;
  }, [viewerHints, voterUserIds]);

  const responseRate =
    totalMembers > 0 ? Math.round((totalVotes / totalMembers) * 100) : 0;

  const chartData = [
    {
      name: "Voted",
      value: totalVotes,
      color: theme.palette.primary.main,
    },
    {
      name: "Not Responded",
      value: notRespondedCount,
      color: theme.palette.warning.main,
    },
    {
      name: "Viewed Only",
      value: Math.max(0, viewedOnlyCount),
      color: theme.palette.grey[300],
    },
  ].filter((item) => item.value > 0);

  const chartDataForDisplay =
    chartData.length > 0
      ? chartData
      : [
          {
            name: "No responses",
            value: 1,
            color: theme.palette.grey[200],
          },
        ];

  if (error) {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        {error}
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      <Paper elevation={0} sx={cardSx}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "primary.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <BarChartOutlinedIcon sx={{ color: "primary.main", fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
              >
                {title}
              </Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                spacing={2}
                sx={{ color: "text.secondary", fontSize: "0.875rem" }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PersonOutlineIcon sx={{ fontSize: 16 }} />
                  <span>{circleLabel}</span>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />
                  <span>{formatPollDate(createdAt)}</span>
                </Stack>
                {viewData && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                    <span>
                      Viewed by {normalizedViews.seen} of {normalizedViews.total}
                      {normalizedViews.total > 0
                        ? ` (${normalizedViews.percentage}%)`
                        : ""}
                    </span>
                  </Stack>
                )}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 1 }}>
                <Chip
                  label={getNoticeFeedTypeChipLabel(typeChip)}
                  size="small"
                  sx={{
                    bgcolor: getNoticeFeedTypeChipBackground(theme, typeChip),
                    color: theme.palette.common.white,
                    fontWeight: 600,
                    fontSize: 12,
                    height: 28,
                    px: 1.5,
                    borderRadius: 2,
                  }}
                />
                <Chip
                  label={circleLabel}
                  size="small"
                  sx={{
                    bgcolor: theme.palette.common.white,
                    color: theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: 12,
                    height: 28,
                    px: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                />
                {results?.isClosed && (
                  <Chip label="Closed" size="small" variant="outlined" />
                )}
              </Stack>
            </Box>
          </Stack>

          {!isPreview && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ flexShrink: 0, width: { xs: "100%", md: "auto" } }}
            >
              <NoticeboardButton
                variant="secondary"
                size="medium"
                startIcon={<NotificationsActiveOutlinedIcon />}
                onClick={() => onSendReminder?.()}
                loading={sendingReminder}
                sx={{ whiteSpace: "nowrap" }}
              >
                Send Reminder
              </NoticeboardButton>
              <Tooltip title="Coming soon">
                <span>
                  <NoticeboardButton
                    variant="secondary"
                    size="medium"
                    startIcon={<EditOutlinedIcon />}
                    disabled
                    sx={{
                      whiteSpace: "nowrap",
                      color: "text.secondary",
                      borderColor: "divider",
                    }}
                  >
                    Edit Poll
                  </NoticeboardButton>
                </span>
              </Tooltip>
              {canDelete && (
                <NoticeboardButton
                  variant="secondary"
                  size="medium"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={onDelete}
                  sx={{
                    whiteSpace: "nowrap",
                    color: "error.main",
                    borderColor: "error.main",
                    "&:hover": {
                      borderColor: "error.dark",
                      bgcolor: "error.50",
                    },
                  }}
                >
                  Delete
                </NoticeboardButton>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "7fr 5fr" },
          gap: 2.5,
        }}
      >
        <Box>
          <Paper elevation={0} sx={{ ...cardSx, height: "100%" }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Poll Results
              </Typography>
              {loading && !results && (
                <Chip label="Updating…" size="small" variant="outlined" />
              )}
              {results && (
                <Chip
                  label={`${totalVotes} vote${totalVotes === 1 ? "" : "s"}`}
                  size="small"
                  sx={{
                    bgcolor: "primary.50",
                    color: "primary.main",
                    fontWeight: 600,
                  }}
                />
              )}
            </Stack>

            <Stack spacing={2.5}>
              {optionsToShow.map((opt) => {
                const claimant = isClaimPoll
                  ? results?.claimedOptions?.find((c) => c.optionId === opt.optionId)
                  : undefined;
                const claimantName =
                  claimant?.voterName && claimant.voterName !== "Unknown voter"
                    ? claimant.voterName
                    : null;
                return (
                <Box key={opt.optionId}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.75 }}
                  >
                    <Box sx={{ minWidth: 0, pr: 1 }}>
                      <Typography variant="body1" fontWeight={500}>
                        {opt.label}
                      </Typography>
                      {claimantName ? (
                        <Typography variant="caption" color="primary.main" fontWeight={500}>
                          Claimed by {claimantName}
                        </Typography>
                      ) : null}
                    </Box>
                    {results ? (
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        {opt.count} ({opt.percentage}%)
                      </Typography>
                    ) : loading ? (
                      <Skeleton variant="text" width={56} />
                    ) : null}
                  </Stack>
                  {results ? (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, opt.percentage)}
                      sx={{
                        height: 10,
                        borderRadius: 1,
                        bgcolor: "primary.50",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 1,
                        },
                      }}
                    />
                  ) : loading ? (
                    <Skeleton variant="rounded" height={10} sx={{ borderRadius: 1 }} />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={0}
                      sx={{ height: 10, borderRadius: 1, opacity: 0.35 }}
                    />
                  )}
                </Box>
                );
              })}
            </Stack>

            {results && totalVotes === 0 && !loading && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                No votes yet. Parents and students vote in the Noticeboard app.
              </Typography>
            )}
          </Paper>
        </Box>

        <Box>
          <Paper elevation={0} sx={{ ...cardSx, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Participation
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "stretch", sm: "center" },
                gap: { xs: 2, sm: 3 },
              }}
            >
              <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <GroupsOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Total Members
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ ml: "auto" }}>
                    {audienceLoading ? "…" : totalMembers}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CheckCircleOutlineIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Votes Received
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ ml: "auto" }}>
                    {totalVotes}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <ScheduleOutlinedIcon sx={{ color: "warning.main", fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    Not Responded
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ ml: "auto" }}>
                    {notRespondedCount}
                  </Typography>
                </Stack>
              </Stack>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ position: "relative", height: 160, mx: "auto", maxWidth: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartDataForDisplay}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={48}
                        outerRadius={68}
                        paddingAngle={chartDataForDisplay.length > 1 ? 2 : 0}
                      >
                        {chartDataForDisplay.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      pointerEvents: "none",
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} color="text.primary">
                      {responseRate}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Response Rate
                    </Typography>
                  </Box>
                </Box>

                <Stack
                  direction="row"
                  flexWrap="wrap"
                  spacing={1}
                  sx={{ mt: 1, justifyContent: "center", gap: 0.75 }}
                >
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Voted ({totalVotes})
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "warning.main",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Not Responded ({notRespondedCount})
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "grey.300",
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Viewed Only ({viewedOnlyCount})
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        <Box>
          <Paper elevation={0} sx={{ ...cardSx, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Who Voted ({voters.length})
            </Typography>
            {voters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No votes yet.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {voters.map((vote) => {
                  const choice =
                    optionLabelById.get(vote.optionId) || "Unknown option";
                  const name = vote.voterName || "Unknown voter";
                  return (
                    <Stack
                      key={`${vote.userId}-${vote.respondedAt}`}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: getAvatarColor(name, theme),
                          fontSize: 14,
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(name)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {name}
                        </Typography>
                        <Typography variant="caption" color="primary.main" fontWeight={500}>
                          Voted for {choice}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        <CalendarTodayOutlinedIcon
                          sx={{ fontSize: 14, color: "text.disabled" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatVoteTimestamp(vote.respondedAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Box>

        <Box>
          <Paper elevation={0} sx={{ ...cardSx, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Not Responded ({notRespondedMembers.length || notRespondedCount})
            </Typography>
            {audienceLoading ? (
              <Stack spacing={1.5}>
                <Skeleton variant="rounded" height={48} />
                <Skeleton variant="rounded" height={48} />
              </Stack>
            ) : notRespondedMembers.length > 0 ? (
              <Stack spacing={1.5}>
                {notRespondedMembers.map((member) => (
                  <Stack
                    key={member.id}
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                  >
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: theme.palette.warning.main,
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {getInitials(member.name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {member.name}
                      </Typography>
                      {member.email && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {member.email}
                        </Typography>
                      )}
                    </Box>
                    {!isPreview && onSendReminder && (
                      <Tooltip title="Send reminder">
                        <IconButton
                          size="small"
                          onClick={() => onSendReminder?.(member)}
                          sx={{ color: "primary.main" }}
                        >
                          <SendOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}
              </Stack>
            ) : notRespondedCount > 0 ? (
              <Typography variant="body2" color="text.secondary">
                {notRespondedCount} member{notRespondedCount === 1 ? "" : "s"} have
                not responded yet.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Everyone has responded.
              </Typography>
            )}
          </Paper>
        </Box>
      </Box>
    </Stack>
  );
};

export default PollDetailsDashboard;
